import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Col, Select, InputNumber, Row, Button, Space, Checkbox } from "antd";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import callApi from "../../constants/API/CallApi";
import "react-phone-input-2/lib/style.css";
import { ShowToast, Severty } from "../../helper/toast";
import { addBelt, ListClass, getBranches } from "../../constants/API/API";
import useApi from "../../hooks/useApi";

const { Option } = Select;
const BeltAdd = ({ visible, setVisible, refresh, branchId }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [orientation, setOrientation] = useState(null); // null, 'isHorizontal', or 'isVertical'

  const handleOk = async (values) => {
    setLoading(true);
    const { schedules, ...rest } = values;

    const payload = {
      ...rest,
    };

    if (orientation === 'isHorizontal') {
      payload.isHorizontal = true;
      payload.isVertical = false;
    } else if (orientation === 'isVertical') {
      payload.isHorizontal = false;
      payload.isVertical = true;
    } else {
      payload.isHorizontal = false;
      payload.isVertical = false;
    }

    callApi({
      callback: addBelt,
      payload,
      onSuccess: ({ message }) => {
        ShowToast(message, Severty.SUCCESS);
        form.resetFields();
        setOrientation(null);
        refresh(new Date().getTime());
        setVisible(false);
        setLoading(false);
      },
      onError: (err) => {
        ShowToast(err, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  const handleCancel = () => {
    setVisible(false);
    form.resetFields();
    setBranches([]);
    setBranchesLoading(false);
    setOrientation(null);
  };

  const validateBeltName = (rule, value) => {
    if (!value) {
      return Promise.reject("Please enter a belt name");
    } else if (!/^[A-Z][a-zA-Z0-9 ]*$/.test(value)) {
      return Promise.reject(
        "Belt name must start with a capital letter and contain only letters, numbers and spaces"
      );
    }
    return Promise.resolve();
  };

  useApi({
    callback: ListClass,
    dependencies: [refresh],
    shouldCallApi: true,
    onSuccess: ({ list }) => {
      setClasses(list);
    },
  });

  useEffect(() => {
    if (visible) {
      setBranchesLoading(true);
      getBranches()
        .then(({ data }) => {
          const branchList = data?.data || [];
          const activeBranches = branchList.filter(
            (branch) => branch.isActive
          );
          setBranches(activeBranches);
          setBranchesLoading(false);
        })
        .catch((err) => {
          ShowToast(err.message || "Failed to fetch branches", Severty.ERROR);
          setBranchesLoading(false);
        });
    }
  }, [visible]);

  useEffect(() => {
    if (visible && branchId) {
      form.setFieldsValue({
        branchId: branchId,
      });
    }
  }, [visible, branchId, form]);

  return (
    <Modal
      open={visible}
      className="tab_modal"
      title="Add Belt"
      okText="Create"
      onCancel={handleCancel}
      okButtonProps={{
        form: "create",
        htmlType: "submit",
        loading: loading,
      }}
      width={800}
      destroyOnClose
    >
      <Form
        id="create"
        form={form}
        onFinish={handleOk}
        layout="vertical"
        preserve={false}
        initialValues={{
          colorCode: ["#000000"],
          isActive: true,
          isDeleted: false,
        }}
      >
        <Row gutter={16}>

          <Col xs={24} md={12}>
            <Form.Item
              name="name"
              label="Belt Name"
              rules={[{ required: true, validator: validateBeltName }]}
            >
              <Input placeholder="Enter belt name" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="stripeCount"
              label="Stripe Count"
              rules={[
                { required: true, message: "Stripe Count is require" },
                {
                  type: "number",
                  min: 0,
                  max: 10,
                  message: "Stripe Count must be between 0 and 10"
                }
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Enter Stripe Count"
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="branchId"
              label="Branch"
              rules={[{ required: true, message: "Please select a branch" }]}
            >
              <Select
                placeholder="Select branch"
                showSearch
                loading={branchesLoading}
                disabled={!!branchId}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {Array.isArray(branches) &&
                  branches.map((branch) => (
                    <Select.Option key={branch._id} value={branch._id}>
                      {branch.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.List name="colorCode">
              {(fields, { add, remove }) => (
                <>
                  <div style={{ marginBottom: 8 }}>Color Code</div>
                  {fields.map((field) => (
                    <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item
                        {...field}
                        rules={[{ required: true, message: "Please enter a color code" }]}
                        style={{ margin: 0 }}
                      >
                        <Input
                          type="color"
                          style={{
                            width: 60,
                            padding: 0,
                            border: "none",
                            background: "none",
                          }}
                        />
                      </Form.Item>
                      {fields.length > 1 && (
                        <MinusCircleOutlined onClick={() => remove(field.name)} style={{ color: 'red', cursor: 'pointer' }} />
                      )}
                    </Space>
                  ))}
                  {fields.length < 3 && (
                    <Form.Item style={{ margin: 0 }}>
                      <Button type="dashed" onClick={() => add("#000000")} block icon={<PlusOutlined />}>
                        Add Color
                      </Button>
                    </Form.Item>
                  )}
                </>
              )}
            </Form.List>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="stripeColor"
              label="Stripe Color Code"
              rules={[
                { required: true, message: "Please enter a stripe color code" }
              ]}
            >
              <Input
                type="color"
                style={{
                  width: 60,
                  padding: 0,
                  border: "none",
                  background: "none",
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="GrowthPromptType"
              label="Growth Prompt Type"
              rules={[
                { required: true, message: "Please select growth prompt type" }
              ]}
            >
              <Select placeholder="Select Prompt Type">
                <Option value="None">None</Option>
                <Option value="Leave a Review">Leave a Review</Option>
                <Option value="Invite a Friend">Invite a Friend</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="reviewLink"
              label="Review Link"
              rules={[
                { required: true, message: "Please enter review link" }
              ]}
            >
              <Input placeholder="Enter Review Link" />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              name="message"
              label="Message"
              rules={[
                { required: true, message: "Message is required" },
                { min: 20, message: "Message must be at least 20 characters" }
              ]}
            >
              <Input.TextArea
                rows={3}
                placeholder="Enter message"
                maxLength={200}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item label="Orientation">
              <Space direction="vertical">
                <Checkbox
                  checked={orientation === 'isHorizontal'}
                  onChange={(e) => setOrientation(e.target.checked ? 'isHorizontal' : null)}
                >
                  Horizontal
                </Checkbox>
                <Checkbox
                  checked={orientation === 'isVertical'}
                  onChange={(e) => setOrientation(e.target.checked ? 'isVertical' : null)}
                >
                  Vertical
                </Checkbox>
              </Space>
            </Form.Item>
          </Col>

        </Row>
      </Form>
    </Modal>
  );
};

export default BeltAdd;
